/**
 * Marie Louise Bag - Standalone Chat Widget
 * Pure vanilla JavaScript - no dependencies, no frameworks
 * 
 * USAGE: Add <script src="/path/to/mlb-chat-widget.js" defer></script> to your HTML
 */

(function() {
  'use strict';

  // ============================================================================
  // CONFIGURATION - Edit these values to customize your widget
  // ============================================================================
  
  const CONFIG = {
    // Backend API endpoint
    API_ENDPOINT: 'https://aiagentscaling.app.n8n.cloud/webhook/chat1',
    
    // Default language/locale
    LANG_DEFAULT: 'it',
    
    // Widget appearance
    WIDGET_TITLE: 'Marie Louise Bag',
    WIDGET_WELCOME: 'Ciao! Come posso aiutarti?',
    
    // Theme colors (use any valid CSS color)
    THEME: {
      primary: '#F4782D',      // Main brand color
      primaryDark: '#D86823',  // Hover/active state
      background: '#ffffff',   // Panel background
      text: '#1a1a1a',        // Main text color
      textLight: '#666666',    // Secondary text
      border: '#e0e0e0',       // Borders
      userBubble: '#F4782D',   // User message background
      botBubble: '#f5f5f5',    // Bot message background
      inputBg: '#f9f9f9'       // Input field background
    },
    
    // Position (bottom-right, bottom-left, top-right, top-left)
    POSITION: 'bottom-right',
    
    // Debug logging
    ENABLE_LOGS: true,
    
    // Retry configuration
    MAX_RETRIES: 1,
    RETRY_DELAY: 1000 // ms
  };

  // ============================================================================
  // INTERNAL STATE & UTILITIES
  // ============================================================================
  
  const STATE = {
    isOpen: false,
    sessionId: null,
    messages: [],
    isLoading: false,
    isMobile: false
  };

  // Detect mobile device
  function detectMobile() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    STATE.isMobile = isMobileDevice || (isTouchDevice && window.innerWidth <= 768);
    return STATE.isMobile;
  }
  
  // Responsive sizing: compute panel width/height based on viewport
  function computePanelSize() {
    try {
      if (!elements || !elements.container) return;
      const W = window.innerWidth;
      const H = window.innerHeight;
      const margin = 20; // Safe margin in px (adjust here)
      let width, height;
      if (W >= 1024) {
        width = Math.min(420, Math.round(W * 0.28));
        height = Math.min(620, Math.round(H * 0.70));
      } else if (W >= 600) {
        width = Math.min(420, Math.round(W * 0.36));
        height = Math.min(560, Math.round(H * 0.70));
      } else {
        width = Math.min(420, Math.round(W * 0.96));
        height = Math.min(680, Math.round(H * 0.78));
      }
      width = Math.max(300, Math.min(width, W - margin * 2));
      height = Math.max(420, Math.min(height, H - margin * 2));
      elements.container.style.setProperty('--panel-w', width + 'px');
      elements.container.style.setProperty('--panel-h', height + 'px');
      elements.container.style.setProperty('--panel-margin', margin + 'px');
    } catch (e) { /* noop */ }
  }
  
  // Logging utility
  function log(...args) {
    if (CONFIG.ENABLE_LOGS) {
      console.log('[MLB-Widget]', ...args);
    }
  }

  // Generate or retrieve session ID
  function getSessionId() {
    if (STATE.sessionId) return STATE.sessionId;
    
    try {
      let sid = localStorage.getItem('mlb_session_id');
      if (!sid) {
        sid = 'mlb-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('mlb_session_id', sid);
      }
      STATE.sessionId = sid;
      return sid;
    } catch (e) {
      // Fallback if localStorage is unavailable
      STATE.sessionId = 'mlb-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
      return STATE.sessionId;
    }
  }

  // Detect document language
  function getLocale() {
    return document.documentElement.lang || CONFIG.LANG_DEFAULT;
  }

  // Escape HTML to prevent XSS
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Normalize URL to ensure it's absolute and valid
  function normalizeUrl(url) {
    if (!url) return null;
    
    // Clean up the URL
    url = url.trim();
    
    // If it's already a valid absolute URL, return it
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // If it starts with //, add https:
    if (url.startsWith('//')) {
      return 'https:' + url;
    }
    
    // If it starts with www., add https://
    if (url.startsWith('www.')) {
      return 'https://' + url;
    }
    
    // If it's a relative path starting with /, try to use current domain
    if (url.startsWith('/')) {
      // Get the current page's origin (protocol + domain)
      const origin = window.location.origin;
      return origin + url;
    }
    
    // If none of the above, assume it needs https://
    if (url.includes('.') && !url.includes(' ')) {
      return 'https://' + url;
    }
    
    // If we can't normalize it, return null
    log('Invalid URL format:', url);
    return null;
  }

  // Enhanced markdown parsing with headers, bold, bullets
  function parseMarkdown(text) {
    if (!text) return '';
    
    // First escape HTML
    let html = escapeHtml(text);
    
    // Remove images and links (keep link text)
    html = html.replace(/!\[[^\]]*?\]\([^)]*\)/g, '');
    html = html.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    html = html.replace(/---+/g, '');
    
    // Headers (### and ##)
    html = html.replace(/^### (.+?)$/gm, '<h3 style="font-size: 1.125rem; font-weight: bold; color: ' + CONFIG.THEME.primary + '; margin: 1rem 0 0.5rem 0;">$1</h3>');
    html = html.replace(/^## (.+?)$/gm, '<h2 style="font-size: 1.25rem; font-weight: bold; color: ' + CONFIG.THEME.primary + '; margin: 1rem 0 0.75rem 0;">$1</h2>');
    
    // Bold: **text**
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight: 600; color: ' + CONFIG.THEME.primary + ';">$1</strong>');
    
    // Bullets: - item
    html = html.replace(/^- (.+?)$/gm, '<div style="display: flex; align-items: flex-start; gap: 0.5rem; margin-bottom: 0.25rem;"><span style="margin-top: 0.35rem; width: 6px; height: 6px; border-radius: 50%; background: ' + CONFIG.THEME.primary + '; flex-shrink: 0;"></span><span>$1</span></div>');
    
    // Line breaks
    html = html.replace(/\n\n+/g, '<br><br>');
    html = html.replace(/\n/g, '<br>');
    html = html.replace(/<br><br><br>/g, '<br><br>');
    html = html.replace(/^\s*<br>/g, '');
    html = html.replace(/<br>\s*$/g, '');
    
    return html.trim();
  }

  // ============================================================================
  // STYLES - Injected into document
  // ============================================================================
  
  function injectStyles() {
    const styleId = 'mlb-widget-styles';
    if (document.getElementById(styleId)) return;
    
    const positions = {
      'bottom-right': 'bottom: 20px; right: 20px;',
      'bottom-left': 'bottom: 20px; left: 20px;',
      'top-right': 'top: 20px; right: 20px;',
      'top-left': 'top: 20px; left: 20px;'
    };
    
    const positionStyle = positions[CONFIG.POSITION] || positions['bottom-right'];
    
    const css = `
      /* MLB Chat Widget Styles */
      #mlb-chat-container {
        position: fixed;
        ${positionStyle}
        z-index: 2147483647;
        /* Responsive panel variables (tuned via JS) */
        --panel-w: 380px;
        --panel-h: 600px;
        --panel-margin: 20px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        font-size: 14px;
        line-height: 1.5;
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
      }
      
      #mlb-chat-button {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: ${CONFIG.THEME.primary};
        color: white;
        border: none;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        position: relative;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }
      
      #mlb-chat-button:active {
        transform: scale(0.95);
      }
      
      #mlb-chat-button:hover {
        background: ${CONFIG.THEME.primaryDark};
        transform: scale(1.05);
        box-shadow: 0 6px 16px rgba(0,0,0,0.2);
      }
      
      #mlb-chat-button:focus {
        outline: 2px solid ${CONFIG.THEME.primary};
        outline-offset: 2px;
      }
      
      #mlb-chat-button svg {
        width: 28px;
        height: 28px;
        fill: currentColor;
      }
      
      #mlb-chat-panel {
        display: none;
        flex-direction: column;
        width: var(--panel-w, 380px);
        height: var(--panel-h, 600px);
        max-width: calc(100vw - (var(--panel-margin, 20px) * 2));
        max-height: calc(100vh - (var(--panel-margin, 20px) * 2));
        background: ${CONFIG.THEME.background};
        border-radius: 16px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.12);
        overflow: hidden;
        position: absolute;
        ${CONFIG.POSITION.includes('bottom') ? 'bottom: 0;' : 'top: 0;'}
        ${CONFIG.POSITION.includes('right') ? 'right: 0;' : 'left: 0;'}
      }
      
      #mlb-chat-panel.open {
        display: flex;
      }
      
      #mlb-chat-header {
        background: ${CONFIG.THEME.primary};
        color: white;
        padding: 16px 20px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-shrink: 0;
      }
      
      #mlb-chat-header h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
      }
      
      .mlb-header-actions {
        display: flex;
        gap: 8px;
        align-items: center;
      }
      
      #mlb-refresh-btn,
      #mlb-close-btn {
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 8px;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
        min-width: 44px;
        min-height: 44px;
      }
      
      #mlb-refresh-btn {
        color: #000000;
      }
      
      #mlb-close-btn {
        color: white;
      }
      
      #mlb-refresh-btn:hover,
      #mlb-close-btn:hover {
        background: rgba(255,255,255,0.1);
      }
      
      #mlb-refresh-btn:focus,
      #mlb-close-btn:focus {
        outline: 2px solid white;
        outline-offset: 2px;
      }
      
      #mlb-refresh-btn svg,
      #mlb-close-btn svg {
        width: 20px;
        height: 20px;
      }
      
      #mlb-messages {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      
      #mlb-messages::-webkit-scrollbar {
        width: 6px;
      }
      
      #mlb-messages::-webkit-scrollbar-thumb {
        background: ${CONFIG.THEME.border};
        border-radius: 3px;
      }
      
      .mlb-message {
        display: flex;
        gap: 8px;
        animation: mlb-fade-in 0.3s ease;
      }
      
      @keyframes mlb-fade-in {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      .mlb-message.user {
        flex-direction: row-reverse;
      }
      
      .mlb-message-bubble {
        max-width: 75%;
        padding: 12px 16px;
        border-radius: 16px;
        word-wrap: break-word;
        -webkit-user-select: text;
        user-select: text;
      }
      
      .mlb-message.user .mlb-message-bubble {
        background: ${CONFIG.THEME.userBubble};
        color: white;
        border-bottom-right-radius: 4px;
      }
      
      .mlb-message.bot .mlb-message-bubble {
        background: ${CONFIG.THEME.botBubble};
        color: ${CONFIG.THEME.text};
        border-bottom-left-radius: 4px;
      }
      
      .mlb-message-bubble a {
        color: ${CONFIG.THEME.primary};
        text-decoration: underline;
      }
      
      .mlb-message.user .mlb-message-bubble a {
        color: white;
      }
      
      .mlb-carousel {
        display: flex;
        gap: 12px;
        overflow-x: auto;
        padding: 12px 32px;
        margin-top: 8px;
        scrollbar-width: none;
        -ms-overflow-style: none;
        -webkit-overflow-scrolling: touch;
        scroll-behavior: smooth;
        scroll-snap-type: x mandatory;
        touch-action: pan-x;
      }
      
      .mlb-carousel::-webkit-scrollbar {
        display: none;
      }
      
      .mlb-carousel-container {
        position: relative;
        width: 100%;
        touch-action: pan-y;
      }
      
      .mlb-carousel-arrow {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 10;
        width: 40px;
        height: 40px;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
        box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }
      
      .mlb-carousel-arrow:active {
        transform: translateY(-50%) scale(0.95);
      }
      
      .mlb-carousel-arrow:hover {
        background: #f9fafb;
        transform: translateY(-50%) scale(1.05);
      }
      
      .mlb-carousel-arrow.left {
        left: 0;
      }
      
      .mlb-carousel-arrow.right {
        right: 0;
      }
      
      .mlb-carousel-arrow.hidden {
        opacity: 0;
        pointer-events: none;
      }
      
      .mlb-product-card {
        flex: 0 0 220px;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        overflow: hidden;
        transition: all 0.2s;
        cursor: pointer;
        text-decoration: none;
        color: inherit;
        display: flex;
        flex-direction: column;
        scroll-snap-align: center;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }
      
      .mlb-product-card:active {
        transform: scale(0.98);
      }
      
      .mlb-product-card:hover {
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        border-color: #d1d5db;
      }
      
      .mlb-product-card:focus {
        outline: 2px solid ${CONFIG.THEME.primary};
        outline-offset: 2px;
      }
      
      .mlb-product-img {
        width: 100%;
        height: 165px;
        object-fit: cover;
        background: #f3f4f6;
      }
      
      .mlb-product-content {
        padding: 12px;
        display: flex;
        flex-direction: column;
        flex: 1;
      }
      
      .mlb-product-title {
        font-size: 14px;
        font-weight: 600;
        color: #111827;
        margin: 0 0 4px 0;
        line-height: 1.3;
      }
      
      .mlb-product-price {
        font-size: 14px;
        font-weight: 600;
        color: #111827;
        margin: 0 0 8px 0;
      }
      
      .mlb-product-description {
        font-size: 12px;
        color: #6b7280;
        line-height: 1.4;
        margin: 0 0 12px 0;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        min-height: 3rem;
        flex: 1;
      }
      
      .mlb-product-button {
        width: 100%;
        background: #f3f4f6;
        color: #111827;
        border: none;
        padding: 12px 16px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
        margin-top: auto;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
        min-height: 44px;
      }
      
      .mlb-product-button:active {
        transform: scale(0.98);
        background: #e5e7eb;
      }
      
      .mlb-typing {
        display: flex;
        gap: 4px;
        padding: 12px 16px;
        background: ${CONFIG.THEME.botBubble};
        border-radius: 16px;
        width: fit-content;
      }
      
      .mlb-typing-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${CONFIG.THEME.textLight};
        animation: mlb-typing 1.4s infinite;
      }
      
      .mlb-typing-dot:nth-child(2) { animation-delay: 0.2s; }
      .mlb-typing-dot:nth-child(3) { animation-delay: 0.4s; }
      
      @keyframes mlb-typing {
        0%, 60%, 100% { opacity: 0.3; transform: scale(0.8); }
        30% { opacity: 1; transform: scale(1); }
      }
      
      #mlb-input-container {
        padding: 16px;
        border-top: 1px solid ${CONFIG.THEME.border};
        display: flex;
        gap: 8px;
        flex-shrink: 0;
        background: ${CONFIG.THEME.background};
      }
      
      #mlb-input {
        flex: 1;
        padding: 12px 16px;
        border: 1px solid ${CONFIG.THEME.border};
        border-radius: 20px;
        font-size: 16px;
        font-family: inherit;
        background: ${CONFIG.THEME.inputBg};
        transition: border-color 0.2s;
        -webkit-appearance: none;
        appearance: none;
        touch-action: manipulation;
        -webkit-user-select: text;
        user-select: text;
      }
        appearance: none;
        touch-action: manipulation;
      }
      
      #mlb-input:focus {
        outline: none;
        border-color: ${CONFIG.THEME.primary};
      }
      
      #mlb-send-btn {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background: ${CONFIG.THEME.primary};
        color: white;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
        flex-shrink: 0;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      }
      
      #mlb-send-btn:active:not(:disabled) {
        transform: scale(0.95);
      }
      
      #mlb-send-btn:hover:not(:disabled) {
        background: ${CONFIG.THEME.primaryDark};
      }
      
      #mlb-send-btn:focus {
        outline: 2px solid ${CONFIG.THEME.primary};
        outline-offset: 2px;
      }
      
      #mlb-send-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      #mlb-send-btn svg {
        width: 18px;
        height: 18px;
      }
      
      .mlb-error {
        background: #ffebee;
        color: #c62828;
        padding: 12px 16px;
        border-radius: 8px;
        font-size: 13px;
        text-align: center;
      }
      
      /* Mobile responsive */
      @media (max-width: 768px) {
        #mlb-chat-container {
          bottom: 20px !important;
          right: 20px !important;
          left: auto !important;
          top: auto !important;
        }
        
        #mlb-chat-button {
          width: 60px;
          height: 60px;
          position: relative;
          bottom: auto;
          right: auto;
        }
        
        #mlb-chat-button svg {
          width: 28px;
          height: 28px;
        }
        
        #mlb-chat-panel {
          /* On small screens, keep compact floating panel using computed vars */
          position: absolute;
          border-radius: 14px;
        }
        
        #mlb-chat-panel:not(.open) {
          display: none !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
        
        #mlb-messages {
          padding: 16px;
          padding-bottom: env(safe-area-inset-bottom, 16px);
        }
        
        #mlb-input-container {
          padding: 12px 16px;
          padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 12px);
          background: ${CONFIG.THEME.background};
        }
        
        #mlb-input {
          font-size: 16px; /* Prevents zoom on iOS */
        }
        
        .mlb-carousel {
          padding: 12px 16px;
        }
        
        .mlb-carousel-arrow {
          width: 36px;
          height: 36px;
        }
        
        .mlb-carousel-arrow.left {
          left: 4px;
        }
        
        .mlb-carousel-arrow.right {
          right: 4px;
        }
        
        .mlb-product-card {
          flex: 0 0 200px;
        }
        
        .mlb-message-bubble {
          max-width: 85%;
        }
      }
      
      @media (max-width: 480px) {
        .mlb-carousel {
          padding: 12px 8px;
        }
        
        .mlb-product-card {
          flex: 0 0 180px;
        }
        
        .mlb-product-img {
          height: 135px;
        }
      }
      
      /* iOS specific fixes */
      @supports (-webkit-touch-callout: none) {
        #mlb-chat-panel {
          /* Respect dynamic viewport height on iOS without forcing full-screen */
          max-height: calc(100dvh - (var(--panel-margin, 20px) * 2));
        }
        
        #mlb-input {
          -webkit-appearance: none;
          border-radius: 20px;
        }
      }
    `;
    
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = css;
    document.head.appendChild(style);
    
    log('Styles injected');
  }

  // ============================================================================
  // DOM CREATION
  // ============================================================================
  
  function createWidget() {
    const container = document.createElement('div');
    container.id = 'mlb-chat-container';
    container.setAttribute('role', 'region');
    container.setAttribute('aria-label', 'Chat widget');
    
    // Chat button
    const button = document.createElement('button');
    button.id = 'mlb-chat-button';
    button.setAttribute('aria-label', 'Open chat');
    button.innerHTML = `
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
      </svg>
    `;
    
    // Chat panel
    const panel = document.createElement('div');
    panel.id = 'mlb-chat-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-labelledby', 'mlb-chat-title');
    panel.setAttribute('aria-modal', 'true');
    
    // Header
    const header = document.createElement('div');
    header.id = 'mlb-chat-header';
    header.innerHTML = `
      <h3 id="mlb-chat-title">${escapeHtml(CONFIG.WIDGET_TITLE)}</h3>
      <div class="mlb-header-actions">
        <button id="mlb-refresh-btn" aria-label="Refresh chat">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
            <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
          </svg>
        </button>
        <button id="mlb-close-btn" aria-label="Close chat">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
    `;
    
    // Messages container
    const messages = document.createElement('div');
    messages.id = 'mlb-messages';
    messages.setAttribute('role', 'log');
    messages.setAttribute('aria-live', 'polite');
    messages.setAttribute('aria-atomic', 'false');
    
    // Input container
    const inputContainer = document.createElement('div');
    inputContainer.id = 'mlb-input-container';
    inputContainer.innerHTML = `
      <input 
        type="text" 
        id="mlb-input" 
        placeholder="Scrivi un messaggio..."
        aria-label="Message input"
        autocomplete="off"
      />
      <button id="mlb-send-btn" aria-label="Send message">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
        </svg>
      </button>
    `;
    
    panel.appendChild(header);
    panel.appendChild(messages);
    panel.appendChild(inputContainer);
    
    container.appendChild(button);
    container.appendChild(panel);
    
    document.body.appendChild(container);
    
    log('Widget DOM created');
    return { 
      container, 
      button, 
      panel, 
      messages, 
      input: inputContainer.querySelector('#mlb-input'), 
      sendBtn: inputContainer.querySelector('#mlb-send-btn'), 
      closeBtn: header.querySelector('#mlb-close-btn'),
      refreshBtn: header.querySelector('#mlb-refresh-btn')
    };
  }

  // ============================================================================
  // CHAT UI FUNCTIONS
  // ============================================================================
  
  let elements = null;
  
  function toggleChat() {
    STATE.isOpen = !STATE.isOpen;
    
    if (STATE.isOpen) {
      elements.panel.classList.add('open');
      
      // Hide button completely on mobile
      if (STATE.isMobile) {
        elements.button.style.display = 'none';
        elements.button.style.visibility = 'hidden';
        elements.button.style.pointerEvents = 'none';
        
        // Lock body scroll
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100%';
        document.body.style.top = '0';
        
        // Focus input with delay to prevent keyboard issues
        setTimeout(() => {
          elements.input.focus();
        }, 300);
      } else {
        elements.button.style.display = 'none';
        elements.input.focus();
      }
      
      // Show welcome message if first time
      if (STATE.messages.length === 0) {
        addBotMessage(CONFIG.WIDGET_WELCOME);
      }
      
      log('Chat opened');
    } else {
      elements.panel.classList.remove('open');
      
      // Show button on mobile
      if (STATE.isMobile) {
        elements.button.style.display = 'flex';
        elements.button.style.visibility = 'visible';
        elements.button.style.pointerEvents = 'auto';
        
        // Unlock body scroll
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.height = '';
        document.body.style.top = '';
        
        // Blur input to close keyboard
        elements.input.blur();
      } else {
        elements.button.style.display = 'flex';
      }
      
      log('Chat closed');
    }
  }
  
  function addUserMessage(text) {
    const msg = { role: 'user', content: text };
    STATE.messages.push(msg);
    renderMessage(msg);
    scrollToBottom();
  }
  
  function addBotMessage(text, items = null) {
    const msg = { role: 'bot', content: text, items };
    STATE.messages.push(msg);
    renderMessage(msg);
    scrollToBottom();
  }
  
  function renderMessage(msg) {
    const messageDiv = document.createElement('div');
    messageDiv.className = `mlb-message ${msg.role}`;
    messageDiv.setAttribute('role', 'article');
    
    const bubble = document.createElement('div');
    bubble.className = 'mlb-message-bubble';
    
    if (msg.role === 'user') {
      bubble.textContent = msg.content;
    } else {
      bubble.innerHTML = parseMarkdown(msg.content);
      
      // Add carousel if items exist
      if (msg.items && msg.items.length > 0) {
        const carousel = createCarousel(msg.items);
        bubble.appendChild(carousel);
      }
    }
    
    messageDiv.appendChild(bubble);
    elements.messages.appendChild(messageDiv);
  }
  
  function createCarousel(items) {
    const container = document.createElement('div');
    container.className = 'mlb-carousel-container';
    
    const carousel = document.createElement('div');
    carousel.className = 'mlb-carousel';
    carousel.setAttribute('role', 'list');
    carousel.setAttribute('aria-label', 'Product recommendations');
    
    // Add navigation arrows
    const leftArrow = document.createElement('button');
    leftArrow.className = 'mlb-carousel-arrow left hidden';
    leftArrow.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#374151" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>';
    leftArrow.setAttribute('aria-label', 'Previous products');
    
    const rightArrow = document.createElement('button');
    rightArrow.className = 'mlb-carousel-arrow right';
    rightArrow.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#374151" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>';
    rightArrow.setAttribute('aria-label', 'Next products');
    
    // Check scroll position to show/hide arrows
    function updateArrows() {
      const scrollLeft = carousel.scrollLeft;
      const scrollWidth = carousel.scrollWidth;
      const clientWidth = carousel.clientWidth;
      
      // Hide arrows on mobile since users can swipe
      if (STATE.isMobile) {
        leftArrow.style.display = 'none';
        rightArrow.style.display = 'none';
        return;
      }
      
      if (scrollLeft <= 0) {
        leftArrow.classList.add('hidden');
      } else {
        leftArrow.classList.remove('hidden');
      }
      
      if (scrollLeft >= scrollWidth - clientWidth - 1) {
        rightArrow.classList.add('hidden');
      } else {
        rightArrow.classList.remove('hidden');
      }
    }
    
    leftArrow.addEventListener('click', (e) => {
      e.preventDefault();
      carousel.scrollBy({ left: -240, behavior: 'smooth' });
    });
    
    rightArrow.addEventListener('click', (e) => {
      e.preventDefault();
      carousel.scrollBy({ left: 240, behavior: 'smooth' });
    });
    
    carousel.addEventListener('scroll', updateArrows);
    
    // Touch support for mobile
    if (STATE.isMobile) {
      let touchStartX = 0;
      let scrollStartLeft = 0;
      
      carousel.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
        scrollStartLeft = carousel.scrollLeft;
      }, { passive: true });
      
      carousel.addEventListener('touchmove', (e) => {
        if (!touchStartX) return;
        const touchX = e.touches[0].clientX;
        const diff = touchStartX - touchX;
        carousel.scrollLeft = scrollStartLeft + diff;
      }, { passive: true });
      
      carousel.addEventListener('touchend', () => {
        touchStartX = 0;
      }, { passive: true });
    }
    
    items.forEach(item => {
      const rawPhotoUrl = item['Photo URL'] || item.photo_url || item.image || item.imageUrl;
      const photoUrl = normalizeUrl(rawPhotoUrl);
      const rawProductUrl = item['Product URL'] || item.product_url || item.url || item.link;
      const productUrl = normalizeUrl(rawProductUrl);
      const title = item['Title'] || item.title || item['Name'] || item.name || '';
      const price = item['Price'] || item.price || '';
      const description = item['Description'] || item.description || '';
      
      if (photoUrl && productUrl) {
        const card = document.createElement('div');
        card.className = 'mlb-product-card';
        card.setAttribute('role', 'listitem');
        card.setAttribute('tabindex', '0');
        
        const img = document.createElement('img');
        img.className = 'mlb-product-img';
        img.src = photoUrl;
        img.alt = title || 'Product';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.referrerPolicy = 'no-referrer';
        img.onerror = function() {
          this.src = 'data:image/svg+xml;base64,' + btoa(`
            <svg width="220" height="165" xmlns="http://www.w3.org/2000/svg">
              <rect width="100%" height="100%" fill="#f3f4f6"/>
              <g transform="translate(110,82.5)">
                <circle cx="-15" cy="-7" r="6" fill="#9ca3af"/>
                <circle cx="15" cy="-7" r="6" fill="#9ca3af"/>
                <rect x="-22" y="4" width="44" height="15" rx="7" fill="#9ca3af"/>
              </g>
            </svg>
          `);
        };
        
        const content = document.createElement('div');
        content.className = 'mlb-product-content';
        
        if (title) {
          const titleEl = document.createElement('h3');
          titleEl.className = 'mlb-product-title';
          titleEl.textContent = title;
          content.appendChild(titleEl);
        }
        
        if (price) {
          const priceEl = document.createElement('p');
          priceEl.className = 'mlb-product-price';
          priceEl.textContent = price;
          content.appendChild(priceEl);
        }
        
        if (description) {
          const descEl = document.createElement('p');
          descEl.className = 'mlb-product-description';
          // Strip HTML tags from description
          const cleanDesc = description.replace(/<[^>]*>/g, '').trim();
          descEl.textContent = cleanDesc.length > 160 ? cleanDesc.substring(0, 160) + '...' : cleanDesc;
          content.appendChild(descEl);
        }
        
        const button = document.createElement('button');
        button.className = 'mlb-product-button';
        button.textContent = 'Shop now';
        button.onclick = function(e) {
          e.stopPropagation();
          window.open(productUrl, '_blank', 'noopener,noreferrer');
        };
        content.appendChild(button);
        
        card.appendChild(img);
        card.appendChild(content);
        
        card.onclick = function(e) {
          if (e.target !== button) {
            window.open(productUrl, '_blank', 'noopener,noreferrer');
          }
        };
        
        carousel.appendChild(card);
      }
    });
    
    container.appendChild(leftArrow);
    container.appendChild(carousel);
    container.appendChild(rightArrow);
    
    // Initial arrow state
    setTimeout(updateArrows, 100);
    
    return container;
  }
  
  function showTyping() {
    const typing = document.createElement('div');
    typing.id = 'mlb-typing-indicator';
    typing.className = 'mlb-message bot';
    typing.innerHTML = `
      <div class="mlb-typing">
        <div class="mlb-typing-dot"></div>
        <div class="mlb-typing-dot"></div>
        <div class="mlb-typing-dot"></div>
      </div>
    `;
    elements.messages.appendChild(typing);
    scrollToBottom();
  }
  
  function hideTyping() {
    const typing = document.getElementById('mlb-typing-indicator');
    if (typing) typing.remove();
  }
  
  function showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'mlb-error';
    errorDiv.textContent = message || 'Si è verificato un errore. Riprova.';
    errorDiv.setAttribute('role', 'alert');
    elements.messages.appendChild(errorDiv);
    scrollToBottom();
  }
  
  function scrollToBottom() {
    setTimeout(() => {
      elements.messages.scrollTop = elements.messages.scrollHeight;
    }, 100);
  }
  
  function setInputDisabled(disabled) {
    elements.input.disabled = disabled;
    elements.sendBtn.disabled = disabled;
    STATE.isLoading = disabled;
  }

  // ============================================================================
  // API COMMUNICATION
  // ============================================================================
  
  async function sendMessage(text, retryCount = 0) {
    if (!text.trim() || STATE.isLoading) return;
    
    const userText = text.trim();
    elements.input.value = '';
    
    addUserMessage(userText);
    setInputDisabled(true);
    showTyping();
    
    const payload = {
      session_id: getSessionId(),
      text: userText,
      locale: getLocale(),
      channel: 'web',
      message: userText, // Some backends expect 'message' field
      sessionId: getSessionId() // Alternative field name
    };
    
    log('Sending message:', payload);
    
    try {
      const response = await fetch(CONFIG.API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      log('Received response:', data);
      
      hideTyping();
      handleResponse(data);
      
    } catch (error) {
      log('Error:', error);
      hideTyping();
      
      // Retry logic
      if (retryCount < CONFIG.MAX_RETRIES) {
        log(`Retrying... (${retryCount + 1}/${CONFIG.MAX_RETRIES})`);
        setTimeout(() => {
          sendMessage(userText, retryCount + 1);
        }, CONFIG.RETRY_DELAY);
      } else {
        showError('Mi dispiace, non riesco a connettermi. Riprova più tardi.');
      }
      
    } finally {
      setInputDisabled(false);
      elements.input.focus();
    }
  }
  
  function handleResponse(data) {
    let outputText = '';
    let items = null;
    
    // Handle different response formats
    if (data.output) {
      // Check if output is a JSON string
      if (typeof data.output === 'string' && data.output.trim().startsWith('{')) {
        try {
          const parsed = JSON.parse(data.output);
          outputText = parsed.output || parsed.text || data.output;
          items = parsed.items || null;
        } catch (e) {
          outputText = data.output;
        }
      } else {
        outputText = data.output;
      }
      
      items = items || data.items || null;
    } else if (data.text) {
      outputText = data.text;
    } else if (data.message) {
      outputText = data.message;
    } else {
      outputText = 'Ricevuto.';
    }
    
    addBotMessage(outputText, items);
  }

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  
  function refreshChat() {
    // Clear messages
    STATE.messages = [];
    elements.messages.innerHTML = '';
    
    // Reset session
    STATE.sessionId = null;
    try {
      localStorage.removeItem('mlb_session_id');
    } catch (e) {
      log('Could not clear session from localStorage');
    }
    
    // Reset input
    elements.input.value = '';
    setInputDisabled(false);
    
    // Show welcome message
    addBotMessage(CONFIG.WIDGET_WELCOME);
    
    log('Chat refreshed');
  }

  function attachEventListeners() {
    // Toggle chat
    elements.button.addEventListener('click', toggleChat);
    elements.closeBtn.addEventListener('click', toggleChat);
    
    // Refresh chat
    elements.refreshBtn.addEventListener('click', refreshChat);
    
    // Send message
    elements.sendBtn.addEventListener('click', () => {
      sendMessage(elements.input.value);
    });
    
    elements.input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage(elements.input.value);
      }
    });
    
    // ESC to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && STATE.isOpen) {
        toggleChat();
      }
    });
    
    // Focus trap
    elements.panel.addEventListener('keydown', (e) => {
      if (e.key === 'Tab' && STATE.isOpen) {
        const focusable = elements.panel.querySelectorAll('button, input, a, [tabindex]:not([tabindex="-1"])');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
    
    log('Event listeners attached');
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================
  
  function ensureViewportMeta() {
    // Check if viewport meta tag exists with proper settings
    let viewport = document.querySelector('meta[name="viewport"]');
    if (!viewport) {
      viewport = document.createElement('meta');
      viewport.name = 'viewport';
      viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.head.appendChild(viewport);
      log('Viewport meta tag added');
    } else {
      // Ensure it has the right content for mobile
      const content = viewport.getAttribute('content');
      if (!content.includes('width=device-width')) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
        log('Viewport meta tag updated');
      }
    }
  }
  
  function init() {
    log('Initializing Marie Louise Bag Chat Widget...');
    
    if (document.getElementById('mlb-chat-container')) {
      log('Widget already initialized');
      return;
    }
    
    // Detect mobile device
    detectMobile();
    log('Mobile detected:', STATE.isMobile);
    
    // Ensure viewport meta tag for mobile
    if (STATE.isMobile) {
      ensureViewportMeta();
    }
    
    injectStyles();
    elements = createWidget();
    attachEventListeners();
    
    // Handle window resize/orientation changes (debounced)
    let resizeTimeout;
    const onResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasMobile = STATE.isMobile;
        detectMobile();
        if (wasMobile !== STATE.isMobile) {
          log('Device type changed, mobile:', STATE.isMobile);
        }
        computePanelSize();
      }, 150);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    
    // Handle iOS keyboard behavior
    if (STATE.isMobile) {
      // Prevent body scroll when chat is open
      const preventBodyScroll = (e) => {
        if (STATE.isOpen && !elements.messages.contains(e.target)) {
          e.preventDefault();
        }
      };
      
      // iOS keyboard handling
      if (/iphone|ipad|ipod/i.test(navigator.userAgent)) {
        elements.input.addEventListener('focus', () => {
          setTimeout(() => {
            if (elements.messages) {
              elements.messages.scrollTop = elements.messages.scrollHeight;
            }
          }, 300);
        });
      }
    }
    
    log('Widget initialized successfully');
  }

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Optional: expose init method for manual initialization
  window.MLBWidget = { init };

})();
